/**
 * Plans.
 *
 * One module, read by three things that must never disagree: the Worker that
 * enforces a limit, the pricing page that advertises it, and the settings screen
 * that shows what is left. A pricing table maintained separately from the code
 * that enforces it is how a customer ends up paying for a number the product
 * does not honour.
 *
 * Every limit here is one the product can actually count today. Nothing is sold
 * that is not built: there is no "priority support" row, no "SSO", and no
 * "unlimited users" while there is still exactly one user per account.
 */

export type PlanId = 'free' | 'team' | 'business';

export interface Plan {
  id: PlanId;
  name: string;
  /** Monthly price in whole units of the currency. */
  price: number;
  currency: string;
  tagline: string;
  /** `null` means no ceiling. */
  limits: {
    evidenceCards: number | null;
    questionnairesPerMonth: number | null;
    draftedAnswersPerMonth: number | null;
    /** Documents attached to the public trust page. */
    trustDocuments: number | null;
  };
  /**
   * Things a plan can do rather than a number it is allowed. Kept separate from
   * `limits` so the enforcement code never has to ask whether 0 means "none" or
   * "off".
   */
  capabilities: {
    /** Seeing what this company told a customer the last time they asked. */
    answerHistory: boolean;
  };
  /** Shown on the pricing card, in order. */
  features: string[];
}

/**
 * What is metered, and what deliberately is not.
 *
 * The paid plans do not cap questionnaires or drafted answers, and that is a
 * decision rather than generosity. This customer's pain arrives in bursts —
 * three reviews in one week of May and nothing until September — so a monthly
 * cap on questionnaires guarantees that the paywall appears in the middle of a
 * live deal. That is the worst possible moment in a product sold on "do not get
 * stuck", and no amount of upgrade prompt makes it feel like anything other than
 * being held up.
 *
 * So the meters are on what grows slowly and predictably: evidence cards and
 * trust page documents. Drafting is uncapped because it costs us nothing —
 * the customer's own model key pays for it, which is what makes "unlimited"
 * something we can honestly print.
 *
 * Free still caps both, because Free is the demonstration and an uncapped
 * demonstration is the product.
 */
export const PLANS: Record<PlanId, Plan> = {
  free: {
    id: 'free',
    name: 'Free',
    price: 0,
    currency: 'USD',
    tagline: 'Enough to answer the questionnaire that just landed.',
    limits: {
      evidenceCards: 20,
      questionnairesPerMonth: 2,
      draftedAnswersPerMonth: 50,
      trustDocuments: 0,
    },
    capabilities: { answerHistory: false },
    features: [
      '20 evidence cards',
      '2 questionnaires a month',
      '50 drafted answers a month',
      'Public trust page',
      'Spreadsheets parsed in your browser, never uploaded',
      'Bring your own model key — no usage billed by us',
    ],
  },
  team: {
    id: 'team',
    name: 'Team',
    price: 149,
    currency: 'USD',
    tagline: 'For the company answering one of these most weeks.',
    limits: {
      evidenceCards: 250,
      questionnairesPerMonth: null,
      draftedAnswersPerMonth: null,
      trustDocuments: 10,
    },
    capabilities: { answerHistory: true },
    features: [
      'Unlimited questionnaires — no cap in the middle of a deal',
      'Unlimited drafted answers, on your own model key',
      '250 evidence cards',
      '10 documents on your trust page, behind an NDA gate',
      'Access log — which company read your pentest report, and when',
      'Answer history — what you told this customer last time',
    ],
  },
  business: {
    id: 'business',
    name: 'Business',
    price: 399,
    currency: 'USD',
    tagline: 'For the company where questionnaires are somebody’s week.',
    limits: {
      evidenceCards: null,
      questionnairesPerMonth: null,
      draftedAnswersPerMonth: null,
      trustDocuments: null,
    },
    capabilities: { answerHistory: true },
    features: [
      'Everything in Team, with no ceilings at all',
      'Unlimited evidence cards',
      'Unlimited trust page documents',
      'Answer history kept for the life of the account',
    ],
  },
};

export const PLAN_ORDER: PlanId[] = ['free', 'team', 'business'];

export function planFor(id: string | null | undefined): Plan {
  return PLANS[(id ?? 'free') as PlanId] ?? PLANS.free;
}

/** The billing period a timestamp falls in, as `YYYY-MM` in UTC. */
export function periodOf(now: number): string {
  const date = new Date(now);
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

export type LimitName = keyof Plan['limits'];

export interface LimitCheck {
  allowed: boolean;
  limit: number | null;
  used: number;
  remaining: number | null;
}

/**
 * Whether `used + wanted` fits under a plan's limit.
 *
 * Returns the numbers rather than a boolean alone, because the interface has to
 * be able to say "3 of 50 left" without recomputing the rule somewhere else.
 */
export function checkLimit(plan: Plan, name: LimitName, used: number, wanted = 1): LimitCheck {
  const limit = plan.limits[name];
  if (limit === null) return { allowed: true, limit: null, used, remaining: null };
  const remaining = Math.max(limit - used, 0);
  return { allowed: used + wanted <= limit, limit, used, remaining };
}
